import React from 'react';
import { Link } from 'react-router-dom';
import ArticleLayout from '../components/ArticleLayout';
import { PAGES } from '../lib/site';

const page = PAGES.find((p) => p.path === '/mini-mental-ou-moca');

const ANSWER =
  'O Mini Mental (MEEM) e o MoCA são instrumentos de rastreio cognitivo pontuados até 30, mas com alvos diferentes. O MoCA foi desenhado para detectar comprometimento cognitivo leve e cobre funções executivas que o MEEM praticamente não avalia. O MEEM é mais breve, mais difundido e tem referências brasileiras consolidadas.';

const COMPARISON = [
  {
    criterio: 'Objetivo original',
    meem: 'Graduar de forma breve o estado cognitivo em contexto clínico e psiquiátrico.',
    moca: 'Detectar comprometimento cognitivo leve, faixa em que o MEEM costuma falhar.'
  },
  {
    criterio: 'Funções executivas',
    meem: 'Cobertura mínima.',
    moca: 'Avaliação explícita, incluindo alternância, abstração e fluência verbal.'
  },
  {
    criterio: 'Efeito teto em alta escolaridade',
    meem: 'Acentuado.',
    moca: 'Menor, por conter itens mais exigentes.'
  },
  {
    criterio: 'Tempo de aplicação',
    meem: 'Mais curto.',
    moca: 'Em torno de 10 a 15 minutos.'
  },
  {
    criterio: 'Referências brasileiras',
    meem: 'Amplas, incluindo Brucki et al. (2003) e Lourenço e Veras (2006).',
    moca: 'Disponíveis, porém menos difundidas na prática assistencial.'
  },
  {
    criterio: 'Acesso e uso',
    meem: 'Instrumento licenciado; o formulário completo não é de livre reprodução.',
    moca: 'Uso condicionado a cadastro e treinamento junto ao detentor dos direitos.'
  }
];

const REFERENCES = [
  'Folstein MF, Folstein SE, McHugh PR. "Mini-mental state": a practical method for grading the cognitive state of patients for the clinician. J Psychiatr Res. 1975;12(3):189-98.',
  'Nasreddine ZS, Phillips NA, Bédirian V, et al. The Montreal Cognitive Assessment, MoCA: a brief screening tool for mild cognitive impairment. J Am Geriatr Soc. 2005;53(4):695-9.',
  'Brucki SMD, Nitrini R, Caramelli P, Bertolucci PHF, Okamoto IH. Sugestões para o uso do Mini-Exame do Estado Mental no Brasil. Arq Neuropsiquiatr. 2003;61(3B):777-81.'
];

const RELATED = [
  {
    to: '/escolaridade',
    label: 'Mini Mental por escolaridade',
    note: 'o efeito teto que motiva a troca de instrumento aparece justamente na alta escolaridade.'
  },
  {
    to: '/diagnostica-alzheimer',
    label: 'O Mini Mental diagnostica Alzheimer?',
    note: 'nenhum dos dois instrumentos fecha diagnóstico — ambos orientam investigação.'
  },
  {
    to: '/mini-mental-e-teste-do-relogio',
    label: 'Mini Mental e teste do relógio',
    note: 'outra forma de capturar função executiva sem trocar de instrumento principal.'
  }
];

export default function MiniMentalOuMoca() {
  return (
    <ArticleLayout page={page} answer={ANSWER} related={RELATED} references={REFERENCES}>
      <section aria-labelledby="comparacao">
        <h2 id="comparacao" className="text-2xl mb-5">MEEM e MoCA lado a lado</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse border border-[var(--border)] rounded-xl overflow-hidden text-sm">
            <thead className="bg-[var(--surface-soft)]">
              <tr>
                <th className="p-4 border border-[var(--border)] font-bold">Critério</th>
                <th className="p-4 border border-[var(--border)] font-bold">Mini Mental (MEEM)</th>
                <th className="p-4 border border-[var(--border)] font-bold">MoCA</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map((row) => (
                <tr key={row.criterio}>
                  <td className="p-4 border border-[var(--border)] font-semibold text-[var(--primary)]">{row.criterio}</td>
                  <td className="p-4 border border-[var(--border)] text-[var(--text-muted)]">{row.meem}</td>
                  <td className="p-4 border border-[var(--border)] text-[var(--text-muted)]">{row.moca}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section aria-labelledby="quando-meem-basta">
        <h2 id="quando-meem-basta" className="text-2xl mb-5">Quando o MEEM tende a ser suficiente</h2>
        <p className="text-[var(--text-muted)] mb-4">
          Em quadros de declínio já estabelecido, com repercussão funcional evidente,
          o MEEM costuma capturar bem a magnitude do comprometimento e serve como
          medida de acompanhamento ao longo do tempo.
        </p>
        <p className="text-[var(--text-muted)]">
          Ele também é a escolha prática quando o cenário exige um instrumento
          rápido, com referências locais bem descritas e comparabilidade com
          aplicações anteriores do mesmo paciente. Trocar de instrumento no meio de
          um seguimento custa a série histórica.
        </p>
      </section>

      <section aria-labelledby="quando-moca-informa-mais">
        <h2 id="quando-moca-informa-mais" className="text-2xl mb-5">Quando o MoCA tende a informar mais</h2>
        <p className="text-[var(--text-muted)] mb-4">
          Diante de queixa cognitiva consistente em pessoa com alta escolaridade e
          MEEM preservado, o cenário clássico é o do falso tranquilizador: o
          instrumento não registra uma alteração que o paciente e a família
          percebem. É aí que a carga executiva do MoCA faz diferença.
        </p>
        <p className="text-[var(--text-muted)]">
          O mesmo vale para suspeitas em que a disfunção executiva domina o quadro,
          incluindo apresentações vasculares e frontais, em que a memória episódica
          pode estar relativamente preservada nas fases iniciais.
        </p>
      </section>

      <section aria-labelledby="escores-nao-intercambiaveis">
        <h2 id="escores-nao-intercambiaveis" className="text-2xl mb-5">Os escores não são intercambiáveis</h2>
        <p className="text-[var(--text-muted)] mb-4">
          Ambos vão até 30 pontos, e essa coincidência produz um erro comum: tratar
          26 no MEEM e 26 no MoCA como equivalentes. Não são. Os instrumentos têm
          composição de itens e dificuldade diferentes, e é esperado que a mesma
          pessoa pontue mais baixo no MoCA.
        </p>
        <p className="text-[var(--text-muted)]">
          Na prática, isso significa que não se deve converter um escore no outro,
          nem comparar aplicações de instrumentos distintos como se fossem a mesma
          série. O acompanhamento exige o mesmo instrumento, aplicado da mesma
          forma — ponto detalhado em{' '}
          <Link to="/quem-pode-aplicar" className=" text-[var(--primary)] hover:underline">
            quem pode aplicar o Mini Mental
          </Link>.
        </p>
      </section>
    </ArticleLayout>
  );
}
